<template>
  <div class="trial-balance">
    <div class="d-flex flex-wrap align-items-center gap-3">
      <ReportChoices label="View" icon="ti-layout-list" :choices="views" :active="filters.view" @select="setView" />
      <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />
    </div>

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Levels</label>
          <select v-model="filters.depth" class="form-select" :disabled="filters.view !== 'tree'">
            <option v-for="choice in depthChoices" :key="String(choice.id)" :value="choice.id">{{ choice.text }}</option>
          </select>
        </div>
        <div class="rp-filter rp-search">
          <label>Search</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Account name or code" />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-arrow-down-left"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Closing Debit</div>
          <div class="rp-card-value">{{ formatMoney(summary.closing_dr) }}</div>
          <div class="rp-card-sub">period debits {{ formatMoney(summary.debit) }}</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-arrow-up-right"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Closing Credit</div>
          <div class="rp-card-value">{{ formatMoney(summary.closing_cr) }}</div>
          <div class="rp-card-sub">period credits {{ formatMoney(summary.credit) }}</div>
        </div>
      </div>
      <div class="rp-card" :class="summary.balanced ? 'tone-teal' : 'tone-red'">
        <span class="rp-card-icon"><i class="ti" :class="summary.balanced ? 'ti-circle-check' : 'ti-alert-triangle'"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Status</div>
          <div class="rp-card-value" :class="summary.balanced ? 'text-success' : 'text-danger'">
            {{ summary.balanced ? 'Balanced' : `Off by ${formatMoney(summary.difference)}` }}
          </div>
          <div class="rp-card-sub">total debit {{ summary.balanced ? '=' : '≠' }} total credit</div>
        </div>
      </div>
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-list-numbers"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Accounts</div>
          <div class="rp-card-value">{{ formatInt(summary.account_count) }}</div>
          <div class="rp-card-sub">with a balance or postings</div>
        </div>
      </div>
    </div>

    <div class="rp-insights" :class="{ 'is-loading': loading }" style="grid-template-columns: 1fr">
      <div class="rp-panel">
        <div class="rp-panel-title">Closing balance by type</div>
        <div class="row g-3">
          <div v-for="entry in typeBars" :key="entry.root" class="col">
            <div class="d-flex justify-content-between small mb-1">
              <span>{{ entry.label }}</span>
              <span class="fw-semibold text-nowrap">{{ balanceText(entry.net) }}</span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :class="entry.tone" :style="{ width: `${entry.percent}%` }"></div></div>
          </div>
        </div>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="fw-semibold">
          {{ filters.view === 'tree' ? 'Grouped trial balance' : 'Trial balance' }}
          <span class="text-muted fw-normal small">· {{ plural(rows.length, 'row') }}</span>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th rowspan="2" class="align-bottom">Account</th>
              <th colspan="2" class="text-center">Opening</th>
              <th colspan="2" class="text-center">Period</th>
              <th colspan="2" class="text-center">Closing</th>
            </tr>
            <tr>
              <th class="text-end">Debit</th>
              <th class="text-end">Credit</th>
              <th class="text-end">Debit</th>
              <th class="text-end">Credit</th>
              <th class="text-end">Debit</th>
              <th class="text-end">Credit</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pagedRows" :key="row.key" :class="{ 'acc-group': row.group }">
              <td>
                <span class="acc-indent" :style="{ width: `${row.level * 1.1}rem` }"></span>
                <button type="button" class="acc-link" title="Open this account's ledger" @click="openLedger(row.code)">
                  {{ row.name }}
                </button>
                <span class="acc-code ms-1">{{ row.code }} · {{ row.type }}</span>
              </td>
              <td class="text-end text-muted">{{ amountText(row.opening_dr) }}</td>
              <td class="text-end text-muted">{{ amountText(row.opening_cr) }}</td>
              <td class="text-end">{{ amountText(row.debit) }}</td>
              <td class="text-end">{{ amountText(row.credit) }}</td>
              <td class="text-end fw-semibold">{{ amountText(row.closing_dr) }}</td>
              <td class="text-end fw-semibold">{{ amountText(row.closing_cr) }}</td>
            </tr>
            <tr v-if="!loading && !pagedRows.length">
              <td colspan="7">
                <div class="rp-empty">
                  <i class="ti ti-scale"></i>
                  <div class="fw-semibold">No balances found</div>
                  <small class="text-muted">Try another period or search.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="!loading">
            <tr>
              <th class="text-end">Total (every ledger account)</th>
              <th class="text-end">{{ formatMoney(summary.opening_dr) }}</th>
              <th class="text-end">{{ formatMoney(summary.opening_cr) }}</th>
              <th class="text-end">{{ formatMoney(summary.debit) }}</th>
              <th class="text-end">{{ formatMoney(summary.credit) }}</th>
              <th class="text-end">{{ formatMoney(summary.closing_dr) }}</th>
              <th class="text-end">{{ formatMoney(summary.closing_cr) }}</th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination v-if="rows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                        :start="pageStart" :end="pageEnd" :total="rows.length" @go="goToPage" />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Approved postings of {{ options.store || 'the store' }}; stock moved to or from another store is balanced by
      Inter-store transfers. Opening is the balance before the period, closing = opening + debits −
      credits. The grouped view adds each group's accounts together; totals always come from the ledger accounts.
      Click an account to open its ledger.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import accountingReport from './accountingReportMixin';

const EMPTY_SUMMARY = {
  opening_dr: 0, opening_cr: 0, debit: 0, credit: 0, closing_dr: 0, closing_cr: 0, difference: 0, balanced: true,
  account_count: 0, by_type: [],
};
const TYPE_TONES = { 1: 'info', 2: 'danger', 3: 'success', 4: 'warning', 5: 'info' };

export default {
  name: 'TrialBalanceReport',
  mixins: [inventoryReport, periodReport, accountingReport],

  reportUrl: apiUrls.accounting_trial_balance_report.get,
  fileName: 'trial_balance',
  columns: [],

  data() {
    return {
      filters: { ...defaultPeriod(), view: 'accounts', depth: 3, search: '' },
      summary: { ...EMPTY_SUMMARY },
    };
  },

  computed: {
    views() {
      return [{ key: 'accounts', label: 'Ledger accounts' }, { key: 'tree', label: 'Grouped' }];
    },

    depthChoices() {
      return [
        { id: 1, text: 'Account types' },
        { id: 2, text: 'Main groups' },
        { id: 3, text: 'Sub-groups' },
        { id: null, text: 'All accounts' },
      ];
    },

    hasActiveFilters() {
      const { view, depth, search } = this.filters;
      return this.periodChanged || view !== 'accounts' || depth !== 3 || !!search.trim();
    },

    visibleRows() {
      return this.rows;
    },

    typeBars() {
      const list = this.summary.by_type.map((t) => ({ ...t, abs: Math.abs(Number(t.net) || 0), tone: TYPE_TONES[t.root] }));
      return this.withPercent(list, 'abs');
    },
  },

  watch: {
    'filters.depth'() { if (this.filters.view === 'tree') this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      const { view, depth } = this.filters;
      return { ...this.periodParams(), view, ...(view === 'tree' && depth ? { depth } : {}) };
    },

    resetExtraFilters() {
      return { ...defaultPeriod(this.today), view: 'accounts', depth: 3 };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
    },

    setView(choice) {
      if (choice.key === this.filters.view) return;
      this.filters.view = choice.key;
      this.onFilterChange();
    },

    printReport() {
      const s = this.summary;
      const search = this.filters.search.trim();
      const levels = this.depthChoices.find((c) => c.id === this.filters.depth)?.text;
      this.printStatement(
        'Trial Balance',
        [this.periodText(), this.filters.view === 'tree' ? `Grouped · ${levels}` : 'Ledger accounts', this.storeLabel(),
          search && `Search: ${search}`],
        [['Closing Debit', this.formatMoney(s.closing_dr)], ['Closing Credit', this.formatMoney(s.closing_cr)],
          ['Status', s.balanced ? 'Balanced' : `Off by ${this.formatMoney(s.difference)}`], ['Accounts', this.formatInt(s.account_count)]],
        [
          { label: 'Code', value: (r) => r.code },
          { label: 'Account', value: (r) => `${'\u00a0\u00a0\u00a0'.repeat(r.level)}${r.name}` },
          { label: 'Opening Dr', value: (r) => this.amountText(r.opening_dr), num: true },
          { label: 'Opening Cr', value: (r) => this.amountText(r.opening_cr), num: true },
          { label: 'Debit', value: (r) => this.amountText(r.debit), num: true },
          { label: 'Credit', value: (r) => this.amountText(r.credit), num: true },
          { label: 'Closing Dr', value: (r) => this.amountText(r.closing_dr), num: true },
          { label: 'Closing Cr', value: (r) => this.amountText(r.closing_cr), num: true },
        ],
        this.rows,
        {
          Account: 'Total', 'Opening Dr': this.formatMoney(s.opening_dr), 'Opening Cr': this.formatMoney(s.opening_cr),
          Debit: this.formatMoney(s.debit), Credit: this.formatMoney(s.credit), 'Closing Dr': this.formatMoney(s.closing_dr),
          'Closing Cr': this.formatMoney(s.closing_cr),
        },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
<style scoped src="./accountingReport.css"></style>
